import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { User2, Mail, Phone, Trash2 } from "lucide-react";
import { useState } from "react";
import { UserEditForm } from "./UserEditForm";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UserCardProps {
  user: {
    id: string;
    email?: string | null;
    full_name?: string | null;
    role: 'salesman' | 'freelancer';
    created_at?: string | null;
    phone?: string | null;
  };
}

export function UserCard({ user }: UserCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: deleteUser } = useMutation({
    mutationFn: async () => {
      // First call the Edge Function to delete the auth user
      const { error: authDeleteError } = await supabase.functions.invoke('delete-user', {
        body: { userId: user.id },
      });

      if (authDeleteError) throw authDeleteError;

      // The profile will be automatically deleted due to the CASCADE constraint
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  return (
    <>
      <div 
        className="p-4 hover:bg-accent/50 flex items-center justify-between"
      >
        <div 
          className="space-y-1 cursor-pointer flex-grow"
          onClick={() => setIsEditing(true)}
        >
          <div className="flex items-center gap-2">
            <User2 className="h-4 w-4" />
            <div className="font-medium">{user.full_name || 'Unnamed User'}</div>
            <Badge 
              variant="secondary"
              className={`text-xs ${
                user.role === 'salesman' 
                  ? 'bg-blue-500 hover:bg-blue-600' 
                  : 'bg-green-500 hover:bg-green-600'
              } text-white`}
            >
              {user.role}
            </Badge>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {user.email && (
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {user.email}
              </div>
            )}
            {user.phone && (
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {user.phone}
              </div>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <UserEditForm 
        user={user}
        open={isEditing}
        onOpenChange={setIsEditing}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={() => deleteUser()}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
      />
    </>
  );
}